import AlertCircle from "@iewcasia/MobiusOverrides/Icons/AlertCircle";
import CheckCircle from "@iewcasia/MobiusOverrides/Icons/CheckCircle";
import InfoCircle from "@iewcasia/MobiusOverrides/Icons/InfoCircle";
import Quote from "@iewcasia/MobiusOverrides/Icons/Quote";
import useCustomQuoteRequired from "@iewcasia/Store/Handlers/Utilities/useCustomQuoteRequired";
import { formatNumberWithCommas } from "@iewcasia/Utilities/Utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import getRealTimeWarehouseInventory from "@insite/client-framework/Store/Data/RealTimeInventory/Handlers/GetRealTimeWarehouseInventory";
import translate from "@insite/client-framework/Translate";
import {
    AvailabilityDto,
    AvailabilityMessageType,
    CartLineModel,
    ProductDto,
    ProductModel,
    ProductSettingsModel,
    WarehouseDto,
} from "@insite/client-framework/Types/ApiModels";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import AlertTriangle from "@insite/mobius/Icons/AlertTriangle";
import Calendar from "@insite/mobius/Icons/Calendar";
import X from "@insite/mobius/Icons/X";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css, FlattenSimpleInterpolation } from "styled-components";

interface OwnProps {
    productId: string;
    availability?: AvailabilityDto;
    unitOfMeasure: string;
    trackInventory: boolean;
    isProductDetailsPage?: boolean;
    failedToLoadInventory?: boolean;
    extendedStyles?: ProductAvailabilityStyles;
}

const mapStateToProps = (state: ApplicationState) => {
    const isProductComparisonPage = getCurrentPage(state).type === "ProductComparePage";
    const quoteOnlySite = (state.context.settings.settingsCollection as any).quoteOnlySettings.isQuoteRequestedForAllProducts;
    return {
        productSettings: getSettingsCollection(state).productSettings,
        showInventory: state.context.showInventory,
        currentPage: getCurrentPage(state),
        customInventorySettings: getSettingsCollection(state).customInventorySettings,
        isProductComparisonPage,
        quoteOnlySite,
    };
};

const mapDispatchToProps = {
    getRealTimeWarehouseInventory,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext &
    HasCartLineContext;

export interface ProductAvailabilityStyles {
    container?: GridContainerProps;
    messageGridItem?: GridItemProps;
    availabilityByWarehouseLinkGridItem?: GridItemProps;
    inventoryMessage?: InjectableCss;
    inventoryOnlyMessage?: InjectableCss;
    messageText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    errorWrapper?: InjectableCss;
    errorText?: TypographyPresentationProps;
    availabilityByWarehouseLink?: LinkPresentationProps;
    availabilityByWarehouseModal?: ModalPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    availabilityByWarehouseTable?: DataTableProps;
    availabilityByWarehouseTableHead?: DataTableHeadProps;
    availabilityByWarehouseTableHeader?: DataTableHeaderProps;
    availabilityByWarehouseTableBody?: DataTableBodyProps;
    availabilityByWarehouseTableRow?: DataTableRowProps;
    availabilityByWarehouseTableCell?: DataTableCellBaseProps;

    /* Verndale Custom Feature - New Styles */
    availabilityTooltipWrapper?: InjectableCss;
    availabilityTooltipTitle?: TypographyPresentationProps;
    availabilityTooltipContent?: TypographyPresentationProps;
    availabilityTooltip?: TooltipPresentationProps;
    messageTableGridItem?: GridItemProps;
}

export const productAvailabilityStyles: ProductAvailabilityStyles = {
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
        `,
    },
    messageGridItem: {
        width: 12,
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    messageTableGridItem: {
        width: 12,
        css: css`
            @media (max-width: 768px) {
                display: flex;
                flex-direction: column;
                gap: 5px;
            }
        `,
    },
    availabilityByWarehouseLinkGridItem: { width: 12 },
    availabilityByWarehouseModal: {
        size: 400,
        cssOverrides: {
            modalContent: css`
                padding: 0;
            `,
            modalTitle: css`
                padding: 10px 10px 10px 20px;
            `,
            headlineTypography: css`
                font-size: 20px;
                margin-bottom: 0;
            `,
        },
    },
    centeringWrapper: {
        css: css`
            height: 100px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: 0 auto;
        `,
    },
    messageText: {
        css: css`
            width: 100%;
            ${wrapInContainerStyles}
        `,
    },
    realTimeText: {
        weight: "bold",
        css: css`
            display: inline-block;
            text-align: left;
            min-width: 25px;

            &::after {
                overflow: hidden;
                display: inline-block;
                vertical-align: bottom;
                animation: ellipsis steps(4, end) 900ms infinite 1s;
                content: "\\2026";
                width: 0;
            }

            @keyframes ellipsis {
                to {
                    width: 1.25em;
                }
            }
        `,
    },
    errorText: {
        weight: "bold",
    },
    availabilityTooltip: {
        cssOverrides: {
            tooltipContainer: css`
                width: 140px !important;
            `,
        },
    },
};

const InventoryMessage = styled.div<{ color: string }>`
    display: flex;
    align-items: center;
    border-radius: 5px;
    margin-right: 20px;

    ${IconWrapper} {
        margin-right: 10px;
    }
`;

const InventoryOnlyMessage = styled.div<{ color: string }>`
    display: flex;
    align-items: center;
    border-radius: 5px;
    margin-right: 20px;
    padding: 0 1px 3px;

    ${IconWrapper} {
        margin-right: 10px;
    }
`;

/* Verndale Custom Feature - Adjust availability color method */
const getAvailabilityColor = (
    productSettings: ProductSettingsModel,
    quoteRequired: boolean,
    availability?: AvailabilityDto,
) => {
    if (quoteRequired) {
        return "info";
    }

    if (!productSettings.showInventoryAvailability) {
        return "common";
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return "danger";
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return "warning";
    }

    return "success";
};

/* Verndale Custom Feature - Duplicate availability color method for icons */
const getAvailabilityIconColor = (
    productSettings: ProductSettingsModel,
    quoteRequired: boolean,
    availability?: AvailabilityDto,
) => {
    if (quoteRequired) {
        return "secondary";
    }

    if (!productSettings.showInventoryAvailability) {
        return "common";
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return "danger";
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return "warning";
    }

    return "success";
};

/* Verndale Custom Feature - Adjust availability icon method */
const getAvailabilityIcon = (
    productSettings: ProductSettingsModel,
    quoteRequired: boolean,
    availability?: AvailabilityDto,
) => {
    if (!productSettings.showInventoryAvailability) {
        return undefined;
    }

    if (quoteRequired) {
        return Quote;
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return X;
    }

    if (availabilityType === AvailabilityMessageType.LowStock) {
        return AlertTriangle;
    }

    return CheckCircle;
};

const showLink = (
    availability: AvailabilityDto | undefined,
    trackInventory: boolean,
    productSettings: ProductSettingsModel,
    isProductDetailsPage?: boolean,
): boolean => {
    if (
        !availability ||
        availability.messageType === AvailabilityMessageType.NoMessage ||
        !trackInventory ||
        !productSettings.showInventoryAvailability ||
        !productSettings.displayInventoryPerWarehouse
    ) {
        return false;
    }

    if (!productSettings.displayInventoryPerWarehouseOnlyOnProductDetail) {
        return true;
    }

    return !!isProductDetailsPage;
};

/* Verndale Custom Feature - Create shared method to build tooltip section */
export const getAvailabilityComponent = ({
    titleKey,
    contentKey,
    styles,
    sharedTooltipTextStyle,
    color,
    iconSrc,
    iconColor,
    tooltipText,
    product,
    cartLine,
    isProductComparisonPage,
}: {
    titleKey?: string;
    contentKey: string;
    styles: ProductAvailabilityStyles;
    sharedTooltipTextStyle: FlattenSimpleInterpolation;
    color: string;
    iconSrc: any;
    iconColor: string;
    tooltipText: any;
    product?: ProductModel;
    cartLine?: CartLineModel;
    isProductComparisonPage?: boolean;
}) => {
    const titleKeySiteMessage = titleKey ? (siteMessage(titleKey) !== titleKey ? siteMessage(titleKey) : "") : "";
    const contentKeySiteMessage = siteMessage(contentKey) !== contentKey ? siteMessage(contentKey) : "";
    const productOrCartline = product ?? cartLine;
    const isBackOrder = productOrCartline?.properties["isBackOrder"]?.toLowerCase() === "true";
    const containsTooltip = `${titleKeySiteMessage.toString()}${contentKeySiteMessage.toString()}`.trim() !== "";

    let messageTextStyle = styles.messageText;
    if (containsTooltip) {
        messageTextStyle = {
            ...messageTextStyle,
            css: css`
                ${messageTextStyle?.css}
                ${sharedTooltipTextStyle}
            `,
        };
    }

    const textContent = containsTooltip ? (
        <InventoryMessage color={color} {...styles.inventoryMessage}>
            <Icon color={iconColor} src={iconSrc} />
            <Typography {...messageTextStyle} color={color} data-test-selector="availabilityMessage">
                {tooltipText}
            </Typography>
        </InventoryMessage>
    ) : (
        <InventoryOnlyMessage color={color} {...styles.inventoryOnlyMessage}>
            <Icon color={iconColor} src={iconSrc} />
            <Typography {...messageTextStyle} color={color} data-test-selector="availabilityMessage">
                {tooltipText}
            </Typography>
        </InventoryOnlyMessage>
    );

    return containsTooltip ? (
        <Tooltip
            {...(isProductComparisonPage ? { ...styles.availabilityTooltip } : "")}
            triggerComponent={textContent}
            text={
                (
                    <StyledWrapper {...styles.availabilityTooltipWrapper}>
                        {titleKey && (
                            <Typography {...styles.availabilityTooltipTitle}>{titleKeySiteMessage}</Typography>
                        )}
                        <Typography {...styles.availabilityTooltipContent}>{contentKeySiteMessage}</Typography>
                    </StyledWrapper>
                ) as any
            }
        ></Tooltip>
    ) : (
        textContent
    );
};

const ProductAvailability = ({
    productId,
    availability,
    unitOfMeasure,
    trackInventory,
    productSettings,
    isProductDetailsPage,
    getRealTimeWarehouseInventory,
    failedToLoadInventory,
    showInventory,
    extendedStyles,

    /* Verndale Custom Feature - New Variables */
    productContext: { product, productInfo },
    cartLine,
    currentPage,
    customInventorySettings,
    isProductComparisonPage,
    quoteOnlySite,
}: Props) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [warehouses, setWarehouses] = useState<WarehouseDto[] | undefined>(undefined);
    const [errorMessage, setErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isTable, setIsTable] = useState(false);
    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };

    const viewAvailabilityByWarehouseClickHandler = () => {
        setIsLoading(true);
        setModalIsOpen(true);
        getRealTimeWarehouseInventory({
            productId,
            unitOfMeasure,
            expand: ["warehouses"],
            onComplete: result => {
                setWarehouses(result.warehouses);
                setErrorMessage(result.errorMessage);
                setIsLoading(false);
            },
        });
    };

    /* Verndale Custom Feature - Verify if product / cartline is quotable */
    // if productInfo is undefined, it means that is a cartline

    const [styles] = useState(() => mergeToNew(productAvailabilityStyles, extendedStyles));
    if (!showInventory) {
        return null;
    }

    if (failedToLoadInventory) {
        return (
            <StyledWrapper {...styles.errorWrapper}>
                <Typography {...styles.errorText}>{siteMessage("RealTimeInventory_InventoryLoadFailed")}</Typography>
            </StyledWrapper>
        );
    }

    /* Verndale Custom Feature - Shared style for tooltip underline */
    const sharedTooltipTextStyle = css`
        text-decoration: underline;
        text-decoration-style: dashed;
        text-underline-offset: 5px;
    `;

    let inventoryTextComponent = <Typography {...styles.realTimeText} />;
    /* Verndale Custom Feature - Available quantity component */
    let availableTextComponent = <></>;
    /* Verndale Custom Feature - Non Returnable component */
    let nonReturnableTextComponent = <></>;
    /* Verndale Custom Feature - Lead time component */
    let leadTimeTextComponent = <></>;
    /* Verndale Custom Feature - Additional Available Qty component */
    let additionalAvailableTextComponent = <></>;

    const quoteRequired = useCustomQuoteRequired(productInfo ? { product, productInfo } : cartLine);

    const productOrCartline = product ?? cartLine;

    if (availability?.messageType !== AvailabilityMessageType.NoMessage && availability?.message?.length) {
        let titleKey = quoteRequired
            ? "ProductAvailability_Quote_Title_Tooltip"
            : "ProductAvailability_Available_Title_Tooltip";
        let contentKey = quoteRequired
            ? "ProductAvailability_Quote_Content_Tooltip"
            : "ProductAvailability_Available_Content_Tooltip";

        const color = getAvailabilityColor(productSettings, quoteRequired, availability);
        let iconSrc = getAvailabilityIcon(productSettings, quoteRequired, availability);
        const iconColor = getAvailabilityIconColor(productSettings, quoteRequired, availability);

        const isQuotedByUser = cartLine && cartLine.properties["isQuotedByUser"]?.toLowerCase() === "true";
        const isPutUpPriceZero =
            productInfo?.pricing?.additionalResults?.spools && productInfo.pricing.actualPrice === 0;

        let availabilityMessage = availability.message as any;

        if (productOrCartline?.properties["isBackOrder"] === "true") {
            const leadTimeValue = productOrCartline?.properties["deliveryLeadTimeWeeks"];
            const hasLeadTime = `${leadTimeValue ?? ""}`.trim() !== "";
            availabilityMessage = (
                <>{translate("Typically ships in {0} week(s)", `${hasLeadTime ? leadTimeValue : "0"}`)}</>
            );
            if(!quoteRequired){
                titleKey = "ProductAvailability_backOrder_Title_Tooltip";
                contentKey = "ProductAvailability_backOrder_Content_Tooltip";
                iconSrc = Calendar;
            }
        }

        inventoryTextComponent = getAvailabilityComponent({
            titleKey,
            contentKey,
            styles,
            sharedTooltipTextStyle,
            color,
            iconSrc,
            iconColor,
            product,
            cartLine,
            isProductComparisonPage,
            tooltipText: (
                <>
                    {quoteRequired || isQuotedByUser || isPutUpPriceZero
                        ? translate("Quote Required")
                        : availabilityMessage}
                </>
            ),
        });
    } else if (availability) {
        inventoryTextComponent = <></>;
    }

    /* Verndale Custom Feature - Available quantity component */
    if (availability && productInfo) {
        const availableQty = productInfo.inventory?.qtyOnHand ?? 0;
        const rawQty = productInfo.inventory?.additionalResults?.additionalQtyAvailable ?? 0;
        const additionalAvailableQty = Number(rawQty);
        const qty = `${formatNumberWithCommas(availableQty)} ${productInfo?.unitOfMeasure ?? ""}`;
        const additionalQty = `${formatNumberWithCommas(additionalAvailableQty)} ${productInfo?.unitOfMeasure ?? ""}`;

        availableTextComponent =
            availableQty > 0 && !quoteRequired && !(customInventorySettings as any)?.hideSalesOrgInventory ? (
                getAvailabilityComponent({
                    contentKey: "ProductAvailability_AvailableInventory_Content_Tooltip",
                    styles,
                    sharedTooltipTextStyle,
                    color: "text.disabled",
                    iconSrc: InfoCircle,
                    iconColor: "common.global.gray01",
                    isProductComparisonPage,
                    tooltipText: (
                        <>
                            {siteMessage("Current_Available_Qty")} {qty}
                        </>
                    ),
                })
            ) : (
                <></>
            );

        additionalAvailableTextComponent =
            additionalAvailableQty > 0 && !quoteRequired ? (
                getAvailabilityComponent({
                    contentKey: "ProductAvailability_AvailableInventory_Content_Tooltip",
                    styles,
                    sharedTooltipTextStyle,
                    color: "text.disabled",
                    iconSrc: InfoCircle,
                    iconColor: "common.global.gray01",
                    isProductComparisonPage,
                    tooltipText: (
                        <>
                            {siteMessage("Additional_Available_Qty")} {additionalQty}
                        </>
                    ),
                })
            ) : (
                <></>
            );
    }

    /* Verndale Custom Feature - Conditions */
    const isCMI = (productOrCartline as any)?.properties?.["isCmiProduct"]?.toLowerCase() === "true";

    /* Verndale Custom Feature - Non Returnable component */
    const isNCNR = productOrCartline?.properties["isCustNCNR"]?.toLowerCase() === "true";
    if (availability && !quoteRequired && isNCNR) {
        nonReturnableTextComponent = getAvailabilityComponent({
            titleKey: "ProductAvailability_NonReturnable_Title_Tooltip",
            contentKey: "ProductAvailability_NonReturnable_Content_Tooltip",
            styles,
            sharedTooltipTextStyle,
            color: "danger",
            iconSrc: AlertCircle,
            iconColor: "danger",
            tooltipText: <>{translate("Non-Returnable")}</>,
            isProductComparisonPage,
        });
    }

    /* Verndale Custom Feature - Non Returnable component */
    const leadTimeValue = productOrCartline?.properties["deliveryLeadTimeWeeks"];
    const hasLeadTime = `${leadTimeValue ?? ""}`.trim() !== "";
    const isBackOrder = productOrCartline?.properties["isBackOrder"]?.toLowerCase() === "true";
    if (availability && !quoteRequired && !isCMI && hasLeadTime && !isBackOrder && !quoteOnlySite) {
        if(availability.messageType !== AvailabilityMessageType.Available){
            leadTimeTextComponent = getAvailabilityComponent({
                titleKey: "ProductAvailability_LeadTime_Title_Tooltip",
                contentKey: "ProductAvailability_LeadTime_Content_Tooltip",
                styles,
                sharedTooltipTextStyle,
                color: "success",
                iconSrc: Calendar,
                iconColor: "success",
                tooltipText: <>{translate("Typically ships in {0} week(s)", `${leadTimeValue}`)}</>,
                isProductComparisonPage,
            });
        }
    }else if(quoteOnlySite && quoteRequired && hasLeadTime){
        leadTimeTextComponent = getAvailabilityComponent({
            titleKey: "ProductAvailability_LeadTime_Title_Tooltip",
            contentKey: "ProductAvailability_LeadTime_Content_Tooltip",
            styles,
            sharedTooltipTextStyle,
            color: "success",
            iconSrc: Calendar,
            iconColor: "success",
            tooltipText: <>{translate("Typically ships in {0} week(s)", `${leadTimeValue}`)}</>,
            isProductComparisonPage,
        });
    }

    useEffect(() => {
        const element = document.querySelector('[data-test-selector="productListCards_Table"]');
        if (element) {
            setIsTable(true);
        }
    }, []);

    return (
        <GridContainer {...styles.container}>
            <GridItem {...(isTable ? styles.messageTableGridItem : styles.messageGridItem)}>
                {inventoryTextComponent}
                {/* Verndale Custom Feature - Add new tooltips */}
                {availableTextComponent}
                {additionalAvailableTextComponent}
                {nonReturnableTextComponent}
                {leadTimeTextComponent}
            </GridItem>

            {showLink(availability, trackInventory, productSettings, isProductDetailsPage) && (
                <GridItem {...styles.availabilityByWarehouseLinkGridItem}>
                    <Link
                        {...styles.availabilityByWarehouseLink}
                        onClick={viewAvailabilityByWarehouseClickHandler}
                        data-test-selector="availabilityByWarehouseLink"
                    >
                        {translate("View Availability by Warehouse")}
                    </Link>
                    <Modal
                        {...styles.availabilityByWarehouseModal}
                        headline={translate("Warehouse Inventory")}
                        isOpen={modalIsOpen}
                        handleClose={modalCloseHandler}
                    >
                        {isLoading && (
                            <StyledWrapper {...styles.centeringWrapper}>
                                <LoadingSpinner {...styles.spinner} />
                            </StyledWrapper>
                        )}
                        {!isLoading && (
                            <>
                                {errorMessage ? (
                                    <div>{errorMessage}</div>
                                ) : (
                                    <DataTable
                                        {...styles.availabilityByWarehouseTable}
                                        data-test-selector="availabilityByWarehouseModal"
                                    >
                                        <DataTableHead {...styles.availabilityByWarehouseTableHead}>
                                            <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                                {translate("Warehouse")}
                                            </DataTableHeader>
                                            <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                                {translate("QTY")}
                                            </DataTableHeader>
                                        </DataTableHead>
                                        <DataTableBody {...styles.availabilityByWarehouseTableBody}>
                                            {warehouses?.map(warehouse => (
                                                <DataTableRow
                                                    {...styles.availabilityByWarehouseTableRow}
                                                    key={warehouse.name}
                                                >
                                                    <DataTableCell
                                                        {...styles.availabilityByWarehouseTableCell}
                                                        data-test-selector="warehouseAvailabilityModal_Description"
                                                    >
                                                        {warehouse.description || warehouse.name}
                                                    </DataTableCell>
                                                    <DataTableCell
                                                        {...styles.availabilityByWarehouseTableCell}
                                                        data-test-selector="warehouseAvailabilityModal_Quantity"
                                                    >
                                                        {warehouse.qty}
                                                    </DataTableCell>
                                                </DataTableRow>
                                            ))}
                                        </DataTableBody>
                                    </DataTable>
                                )}
                            </>
                        )}
                    </Modal>
                </GridItem>
            )}
        </GridContainer>
    );
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withProductContext(withCartLine(ProductAvailability as any)) as any);
